import { useCallback, useEffect, useState } from 'react';
import { api, fmtDate, type Me, setCsrfToken, useApi } from './api';
import type { DashboardLayout, PageKey } from './data/types';
import { Analytics } from './pages/Analytics';
import { Automations } from './pages/Automations';
import { Content } from './pages/Content';
import { Dashboard } from './pages/Dashboard';
import { EventsGoals } from './pages/EventsGoals';
import { Integrations } from './pages/Integrations';
import { Login } from './pages/Login';
import { Plans } from './pages/Plans';
import { Services } from './pages/Services';
import { Settings } from './pages/Settings';
import { Studio } from './pages/Studio';
import { Icon } from './ui';

const NAV: [PageKey, string, string][] = [
  ['dashboard', 'Dashboard', 'ph-gauge'],
  ['events', 'Events & Goals', 'ph-flag'],
  ['plans', 'Plans', 'ph-flow-arrow'],
  ['content', 'Content', 'ph-image-square'],
  ['analytics', 'Analytics', 'ph-chart-bar'],
  ['studio', 'Marketing Studio', 'ph-chats-circle'],
  ['automations', 'Automations', 'ph-lightning'],
  ['integrations', 'Integrations', 'ph-plugs'],
  ['services', 'Services', 'ph-cpu'],
  ['settings', 'Settings', 'ph-gear'],
];

const initialPage = (): PageKey => {
  const p = new URLSearchParams(window.location.search).get('page');
  return NAV.some(([k]) => k === p) ? (p as PageKey) : 'dashboard';
};
const initialLayout = (): DashboardLayout => {
  const l = new URLSearchParams(window.location.search).get('layout');
  return l === 'signals' || l === 'portfolio' ? l : 'loop';
};

/** Sign-in gate: the app shell renders only with a valid session (P2-03). */
export function App() {
  const [me, setMe] = useState<Me | 'loading' | 'anonymous'>('loading');
  const refresh = useCallback(async () => {
    try {
      const m = await api<Me>('GET', '/api/v1/auth/me');
      setCsrfToken(m.csrfToken);
      setMe(m);
    } catch {
      setMe('anonymous');
    }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);

  if (me === 'loading') return null;
  if (me === 'anonymous') return <Login onSignedIn={refresh} />;
  return <Shell me={me} onSignedOut={() => setMe('anonymous')} />;
}

function Shell({ me, onSignedOut }: { me: Me; onSignedOut: () => void }) {
  const [page, setPage] = useState<PageKey>(initialPage);
  const [brandId, setBrandId] = useState(me.brands[0]?.id ?? '');
  const [layout, setLayout] = useState<DashboardLayout>(initialLayout);
  const recs = useApi<{ recommendations: unknown[] }>(brandId ? `/api/v1/brands/${brandId}/recommendations` : null);
  const status = useApi<{ snapshots: { criticalDue24h: number; collected: number; missed: number }; localAi: { connected: boolean }; lastSyncAt: string | null }>(brandId ? `/api/v1/brands/${brandId}/status` : null);
  const openRecs = recs.data?.recommendations.length ?? 0;
  const go = (p: PageKey) => () => setPage(p);
  const brand = me.brands.find((b) => b.id === brandId);
  const canEdit = brand?.role === 'ADMIN' || brand?.role === 'EDITOR';
  const snap = status.data?.snapshots;
  const statusLines = [
    { name: 'Snapshots · 24 h critical', value: snap ? (snap.criticalDue24h ? `${snap.collected}/${snap.criticalDue24h}` : 'none due') : '…' },
    { name: 'Local AI worker', value: status.data ? (status.data.localAi.connected ? 'connected' : 'offline') : '…' },
  ];
  const brandName = brand?.name ?? '—';
  const signOut = async () => {
    await api('POST', '/api/v1/auth/logout').catch(() => {});
    onSignedOut();
  };

  if (!brand) {
    return <div style={{ padding: 40 }}><p>Your account has no brand access yet. Ask a workspace admin to grant a brand role.</p><button className="btn btn-secondary" onClick={signOut}>Sign out</button></div>;
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '224px minmax(0, 1fr)', minHeight: '100vh', background: 'var(--color-bg)', color: 'var(--color-text)', fontFamily: 'var(--font-body)', fontSize: 14 }}>
      <aside style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', padding: 'var(--space-6) var(--space-4)', borderRight: '1px solid var(--color-divider)', position: 'sticky', top: 0, height: '100vh' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 var(--space-2)' }}>
          <span style={{ width: 22, height: 22, borderRadius: 6, border: '1.5px solid var(--color-accent)', display: 'grid', placeItems: 'center', color: 'var(--color-accent)' }}><Icon name="ph-pulse" style={{ fontSize: 14 }} /></span>
          <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 500, fontSize: 16, letterSpacing: '-0.01em' }}>Marketing OS</span>
        </div>
        <div className="field">
          <label htmlFor="brand-select">Brand</label>
          <select id="brand-select" className="input" value={brandId} onChange={(e) => setBrandId(e.target.value)} style={{ cursor: 'pointer' }}>
            {me.brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {NAV.map(([key, label, icon]) => {
            const active = page === key;
            return (
              <button key={key} className="nav-btn" onClick={go(key)} aria-current={active ? 'page' : undefined}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 'var(--radius-md)', border: 0, textAlign: 'left', cursor: 'pointer', fontSize: 14, color: active ? 'var(--color-accent)' : 'var(--color-text)', background: active ? 'var(--color-accent-900)' : 'transparent' }}>
                <Icon name={icon} style={{ fontSize: 17, width: 18 }} />
                <span style={{ flex: 1 }}>{label}</span>
                {key === 'dashboard' && openRecs > 0 && <span className="tag tag-accent" style={{ padding: '1px 7px', whiteSpace: 'nowrap', flex: 'none' }}>{openRecs}</span>}
              </button>
            );
          })}
        </nav>
        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 6, padding: 'var(--space-3)', borderRadius: 'var(--radius-md)', background: 'var(--color-surface)', fontSize: 12 }}>
          {statusLines.map((s) => (
            <div key={s.name} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', border: '1px solid var(--color-accent)' }} />
              <span>{s.name}</span>
              <span className="text-muted" style={{ marginLeft: 'auto' }}>{s.value}</span>
            </div>
          ))}
          <div className="text-muted">Last sync {fmtDate(status.data?.lastSyncAt)}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, borderTop: '1px solid var(--color-divider)', paddingTop: 6 }}>
            <span className="text-muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={me.user.email}>{me.user.email}</span>
            <button className="btn btn-ghost" style={{ fontSize: 12, padding: '2px 4px' }} onClick={signOut}>Sign out</button>
          </div>
        </div>
      </aside>

      <main style={{ padding: 'var(--space-8) 40px 64px', display: 'flex', flexDirection: 'column', gap: 'var(--space-8)', maxWidth: 1320 }}>
        {page === 'dashboard' && <Dashboard key={brandId} brandId={brandId} brand={brandName} layout={layout} setLayout={setLayout} onBrand={(id) => setBrandId(id)} goStudio={go('studio')} goPlans={go('plans')} onDecided={recs.reload} />}
        {page === 'events' && <EventsGoals key={brandId} brandId={brandId} brand={brandName} canEdit={canEdit} canAdmin={brand.role === 'ADMIN'} />}
        {page === 'plans' && <Plans key={brandId} brandId={brandId} brand={brandName} canEdit={canEdit} goStudio={go('studio')} goContent={go('content')} goEvents={go('events')} />}
        {page === 'content' && <Content key={brandId} brandId={brandId} brandName={brandName} />}
        {page === 'analytics' && <Analytics key={brandId} brandId={brandId} brand={brandName} />}
        {page === 'studio' && <Studio key={brandId} brandId={brandId} brand={brandName} canEdit={canEdit} />}
        {page === 'automations' && <Automations key={brandId} brandId={brandId} brand={brandName} canEdit={canEdit} canAdmin={brand.role === 'ADMIN'} />}
        {page === 'integrations' && <Integrations key={brandId} brandId={brandId} brandName={brandName} canAdmin={brand.role === 'ADMIN'} />}
        {page === 'services' && <Services isAdmin={me.user.isWorkspaceAdmin} />}
        {page === 'settings' && <Settings key={brandId} brandId={brandId} brand={brandName} canAdmin={brand.role === 'ADMIN'} />}
      </main>
    </div>
  );
}
